import { useNotificationStore } from '@/store/notificationStore';
import { useActiveUrlStore } from '@/store/activeUrlStore';
import { useUrlSourcesStore } from '@/store/urlSourcesStore';
import { useUrlsStore } from '@/store/urlsStore';
import { determineUrlTypeFromSource } from '@/helpers/urlSourceType/determineUrlTypeFromSource';
import { isPathnameMatch } from '@/helpers/isPathnameMatch';
import IUrl from '@/interfaces/IUrl';
import IPageSourceResponse from '@/interfaces/IPageSourceResponse';
import { fetchPageSourceByUrl } from '@/services/scrapeFetchService';
import Provider from '@/enums/Provider';

export async function processSingleUrlFetch(url: IUrl): Promise<void> {
    const activeUrl = useActiveUrlStore();
    const notification = useNotificationStore();
    const urlsStore = useUrlsStore();
    const urlSourcesStore = useUrlSourcesStore();

    let fullUrl: string;
    try {
        fullUrl = new URL(url.pathname, activeUrl.activeUrl).href;
    } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Unknown error';
        notification.addErrorNotification(`Invalid URL: ${url.pathname} - Error: ${errorMessage}`);
        urlsStore.markFetchFailed(url.pathname);
        return;
    }

    const { data, statusCode } = await fetchPageSourceByUrl(fullUrl);
    if (statusCode === null) {
        notification.addWarningNotification(`Fetch aborted ${fullUrl}`);
        return;
    }

    if (statusCode === 0) {
        notification.addErrorNotification(`Fetch failed ${fullUrl}`);
        urlsStore.markFetchFailed(url.pathname);
        return;
    }

    if (statusCode !== 200) {
        notification.addErrorNotification(`Status Code not 200 ${fullUrl} - Status Code: ${statusCode}`);
        urlsStore.markFetchFailed(url.pathname);
        return;
    }

    if (!data) {
        notification.addErrorNotification(`No data received for ${fullUrl}`);
        urlsStore.markFetchFailed(url.pathname);
        return;
    }

    let pageSourceResponse: IPageSourceResponse;
    try {
        pageSourceResponse = JSON.parse(data) as IPageSourceResponse;
    } catch {
        notification.addErrorNotification(`Invalid JSON in response for ${fullUrl}`);
        urlsStore.markFetchFailed(url.pathname);
        return;
    }

    if (pageSourceResponse.status !== 'success') {
        notification.addErrorNotification(`Non success statuses for ${fullUrl}`);
        urlsStore.markFetchFailed(url.pathname);
        return;
    }

    const pageSourceHtml = pageSourceResponse.data?.httpResponse;
    if (!pageSourceHtml) {
        notification.addErrorNotification(`No httpResponse data for ${fullUrl}`);
        urlsStore.markFetchFailed(url.pathname);
        return;
    }

    if (!isPathnameMatch(url.pathname, pageSourceHtml)) {
        notification.addWarningNotification(`URL mismatch: ${url.pathname} does not match the response.`);
        urlsStore.markFetchedAsRedirected(url.pathname);
        return;
    }

    let actualType = url.urlType;
    // can remove provider check once determineUrlTypeFromSource is updated to handle all providers 
    if (activeUrl.getProvider === Provider.IO) {
        actualType = determineUrlTypeFromSource(pageSourceHtml, activeUrl.getProvider) ?? url.urlType;
    }

    if (actualType && actualType !== url.urlType) {
        urlsStore.markFetchedWithTypeCorrection(url.pathname, actualType);
    } else {
        urlsStore.markFetchSuccessful(url.pathname);
    }

    urlSourcesStore.addMappedSiteSourceFromPageSourceHtml(url.pathname, pageSourceHtml, url.urlType);

}
